/* eslint-disable @typescript-eslint/no-unsafe-assignment */
'use client'

import _, { get, merge } from 'lodash'
import useHydrateFormWithUserData from './useHydrateFormWithUserData'
import useI18nZodErrorMap from '@/i18n/zod/useI18nZodErrorMap'
import useLocale from '@/i18n/useLocale'
import useTriggerOnFormValueChanges from './useTriggerOnFormValueChanges'
import { AppError, fromErrorObject, toErrorObject } from '@/helpers/errors'
import {
  type BaseSyntheticEvent,
  type FormEvent,
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react'
import { type FieldPath, type Path, useForm } from 'react-hook-form'
import { createSyntheticEvent } from '@/helpers/utils'
import { delay } from '@betterplace/utils'
import { errorToFieldErrors, fromFormData, toFormData } from '@/form/utils'
import { useTranslations } from 'next-intl'
import { zodResolver } from '@hookform/resolvers/zod'
import type { BaseFormOperation, BaseFormState } from '@/form/types'
import type { ImperativeSubmit, SetterTools, UseActionFormProps, UseActionFormReturn } from './types'
import type { z } from 'zod'

/**
 * This hook combines a form action (that you can create by calling toFormAction on a normal server action)
 * to provide a server-side validation, useFormState from React, for cross-call server-side state persistence
 * and React Hook Form for client-side validation and client, it's return is mostly compatible with useForm from RHF
 * @param options useActionForm configuration. See UseActionFormProps for more details
 * @returns
 */
function useActionForm<
  TSchema extends z.Schema,
  TOperation extends BaseFormOperation,
  TState extends BaseFormState<TOperation>,
>({
  action,
  actionContext,
  schema,
  defaultValues: defaultValues_,
  getErrorMap,
  beforeFormSubmit,
  afterClientSubmitError,
  afterClientSubmitSuccess,
  afterSubmitSuccess,
  afterSubmitError,
  defaultErrorMessage,
  id: externalId,
  validateAll,
  ...props
}: UseActionFormProps<TSchema, TOperation, TState>): UseActionFormReturn<z.infer<TSchema>, TState['data']> {
  const locale = useLocale()
  const [serverError_, setServerError_] = useState<AppError | null>(null)
  const t = useTranslations('nextjs')
  useI18nZodErrorMap()
  const [serverState, clientAction, pending] = useActionState(action, {
    ...actionContext,
    locale,
    fields: toFormData(defaultValues_),
  } as Awaited<TState>)

  const defaultValues = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    () => ({ ...defaultValues_, ...fromFormData<z.infer<TSchema>>(serverState.fields) }),
    [serverState.fields, defaultValues_]
  )

  const [submitActionInProgress, startSubmitAction] = useTransition()
  const [busy, setBusy] = useState(false)
  const resolver = useMemo(
    () => zodResolver(schema, { errorMap: getErrorMap?.(t, locale) }),
    [schema, getErrorMap, t, locale]
  )

  const {
    handleSubmit,
    formState: clientFormState,
    getValues,
    reset: reset_,
    setError,
    setFocus,
    setValue,
    clearErrors,
    resetField,
    watch,
    trigger,
    ...rest
  } = useForm({
    resolver,
    defaultValues,
    // errors: external,
    ...props,
  })

  const { id } = useHydrateFormWithUserData(setValue, defaultValues_, externalId)

  const tools: SetterTools<z.infer<TSchema>> = useMemo(
    () => ({ setBusy, setError, setFocus, setValue, clearErrors, resetField }),
    [clearErrors, setError, setFocus, setValue, resetField]
  )

  const handleServerError = useCallback(
    async (serverError: AppError) => {
      const input = getValues()
      for (const e of serverError.errors) {
        if (!('path' in e)) continue
        const fieldPath = e.path.join('.') as FieldPath<z.infer<TSchema>>
        tools.setValue(fieldPath, get(input, fieldPath), { shouldTouch: true, shouldValidate: true })
        tools.resetField(fieldPath, {
          keepTouched: true,
          keepDirty: false,
          keepError: true,
          defaultValue: getValues(fieldPath),
        })
      }
      setServerError_(serverError)
      if (typeof afterSubmitError !== 'function') return
      // ensure the afterSubmitError is called on the next tick
      await delay(0)
      const errors = toErrorObject<z.infer<TSchema>>(serverError.errors)
      await afterSubmitError(errors, input, tools, { shouldSetErrors: true })
    },
    [getValues, afterSubmitError, tools]
  )

  const onSubmitInner = useCallback(
    async (input: z.infer<TSchema>, event?: BaseSyntheticEvent) => {
      if (!event) {
        throw new TypeError('Event is missing')
      }
      await afterClientSubmitSuccess?.(input, tools)
      const formData = toFormData(input)
      let result = await action(serverState, formData)
      // in case we have an uncaught error
      if (!result) {
        result = {
          error: new AppError([
            {
              message: t('errors.messages.try_again'),
              type: 'GeneralError',
            },
          ]),
          data: undefined,
          locale,
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { data, error } = result

      if (error) {
        await handleServerError(result.error)
      } else {
        setServerError_(null)
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        await afterSubmitSuccess?.(result.data, input, tools)
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      return { data, error }
    },
    [afterClientSubmitSuccess, tools, action, serverState, t, locale, afterSubmitSuccess, handleServerError]
  )

  const serverErrors = useMemo(() => {
    return errorToFieldErrors(serverError_ ?? serverState.error, defaultErrorMessage)
  }, [serverError_, serverState.error, defaultErrorMessage])

  /**
   * Fields to be merged with client-side errors, we assume that dirty fields do
   * were already processed by the user, so the error can go away
   */
  const activeServerErrors = useMemo(() => {
    const { dirtyFields } = clientFormState
    const errors =
      (serverError_ ?? serverState.error)?.errors.filter((error) => {
        const path = 'path' in error ? error.path : []
        if (!path.length) return true
        const pathStr = path.join('.')
        const dirty = get(dirtyFields, pathStr)
        return !dirty
      }) ?? []
    return errorToFieldErrors({ errors }, defaultErrorMessage)
  }, [serverError_, serverState.error, defaultErrorMessage, clientFormState])

  const serverStateIsValid = useMemo(() => {
    return !serverState.error?.errors.length
  }, [serverState.error])

  const formState = useMemo(() => {
    const {
      errors,
      isValid,
      isDirty,
      isLoading,
      isSubmitSuccessful,
      isSubmitted,
      isSubmitting,
      isValidating,
      submitCount,
      dirtyFields,
      touchedFields,
      disabled,
      validatingFields,
      isReady,
    } = clientFormState // clientFormState is a Proxy, so we cannot simply spread values here

    return {
      submitCount,
      dirtyFields,
      touchedFields,
      isDirty,
      isLoading,
      isSubmitSuccessful,
      isSubmitted,
      isSubmitting: isSubmitting || busy || submitActionInProgress || pending,
      isValidating,
      isValid: isValid && serverStateIsValid,
      disabled,
      validatingFields,
      isReady,
      errors: merge(activeServerErrors, errors),
    }
  }, [clientFormState, serverStateIsValid, activeServerErrors, busy, submitActionInProgress, pending])

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      startSubmitAction(async () => {
        const values = getValues()
        await beforeFormSubmit?.(values, tools)
        await handleSubmit(onSubmitInner, async (errors) => {
          const array = fromErrorObject(errors)
          for (const err of array) {
            if (err.type === 'FieldError') {
              const field = err.path.join('.') as Path<z.infer<TSchema>>
              setValue(field, values[field], { shouldTouch: true })
              continue
            }
          }
          await afterClientSubmitError?.(errors, values, tools)
        })(event)
      })
    },
    [getValues, beforeFormSubmit, tools, handleSubmit, onSubmitInner, afterClientSubmitError, setValue]
  )

  const submit: ImperativeSubmit<z.infer<TSchema>, TState['data']> = useCallback(() => {
    return new Promise((resolve, reject) => {
      startSubmitAction(async () => {
        try {
          await beforeFormSubmit?.(getValues(), tools)
          await handleSubmit(
            async (input) => {
              const formEvent = createSyntheticEvent(
                new FormDataEvent('submit', { cancelable: true, formData: new FormData() })
              )
              const res = await onSubmitInner(input, formEvent)
              if (res.data) {
                return resolve({ data: res.data, input })
              }

              // if we have only field errors these are simply validation errors
              if (!res.error?.errors.find((error) => error.type !== 'FieldError')) {
                return reject(new Error(ActionFormSubmitErrorTypes.ValidationError))
              }
              reject(
                new Error(`${ActionFormSubmitErrorTypes.ServerError} - errors: ${JSON.stringify(res.error?.errors)}`)
              )
            },
            async (errors) => {
              const values = getValues()
              await afterClientSubmitError?.(errors, values, tools)
              reject(new Error(ActionFormSubmitErrorTypes.ValidationError))
            }
          )()
        } catch (error) {
          reject(error)
        }
      })
    })
  }, [beforeFormSubmit, getValues, tools, handleSubmit, onSubmitInner, afterClientSubmitError])

  // immediately trigger validation for the default values
  useEffect(() => {
    void trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useTriggerOnFormValueChanges({ state: clientFormState, trigger, watch, validateAll })

  const reset: typeof reset_ = useCallback((v) => (v ? reset_(v) : reset_(defaultValues_)), [reset_, defaultValues_])

  return {
    ...rest,
    reset,
    setBusy,
    setFocus,
    setValue,
    resetField,
    setError,
    clearErrors,
    getValues,
    submit,
    formState,
    handleSubmit,
    onSubmit,
    defaultValues,
    watch,
    trigger,
    action: clientAction,
    id,
    serverErrors,
  }
}

export const ActionFormSubmitErrorTypes = {
  ValidationError: 'ACTION_FORM_VALIDATION_ERROR',
  ServerError: 'ACTION_FORM_SERVER_ERROR',
} as const
export default useActionForm
